import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { OcrService } from '../../../services/ocr/ocr-service'

const messages = ['style.ocrDownloadModalMessage']

interface OcrDownloadModalProps {
  onDone: () => void
}

export const OcrDownloadModal = ({ onDone }: OcrDownloadModalProps) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState(OcrService.preloadStatus)
  const [messageIndex, setMessageIndex] = useState(0)

  useEffect(() => {
    const unsub = OcrService.subscribe(() => {
      setStatus(OcrService.preloadStatus)
    })
    OcrService.preload()
    return unsub
  }, [])

  useEffect(() => {
    if (status !== 'loading') return
    const interval = setInterval(() => {
      setMessageIndex(i => (i + 1) % messages.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [status])

  if (status === 'idle') return null

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 animate-in fade-in duration-200">
      <div className="bg-bg-surface rounded-2xl shadow-2xl border border-border-default p-8 max-w-sm w-full mx-4 animate-in zoom-in-95 duration-200 text-center">
        {status === 'loading' && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 border-4 border-fg-muted/20 border-t-accent rounded-full animate-spin" />
            <h3 className="text-lg font-semibold text-fg-primary mb-2">{t('style.ocrDownloadModalTitle')}</h3>
            <p className="text-sm text-fg-muted">{t(messages[messageIndex]!)}</p>
          </>
        )}
        {status === 'ready' && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-fg-primary mb-2">{t('style.ocrDownloadModalTitle')}</h3>
            <p className="text-sm text-fg-muted mb-6">{t('style.ocrDownloadModalReady')}</p>
            <button
              onClick={onDone}
              className="px-6 py-2.5 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
            >
              {t('style.ocrDownloadModalGotIt')}
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-fg-primary mb-2">{t('style.ocrDownloadModalTitle')}</h3>
            <p className="text-sm text-fg-muted mb-6">{t('style.ocrLoadingModels') || 'Failed to download models. Please try again.'}</p>
            <button
              onClick={onDone}
              className="px-6 py-2.5 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
            >
              {t('style.ocrModalCancel') || 'Close'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
